/* implBasis.js -- THE FEE BASIS OF AN IMPLEMENTATION LINE (Brian, 2026-10-27; diffs/impl-pct-investigation.txt Q4).
 *
 * An implementation line's fee is computed one of two ways:
 *   per client    (avg price, fee per client, take rate) -- new clients = round(new ACV / avg price) x fee x take rate
 *   % of new ACV  the new annual contract value booked that month x a percentage (Brian's model: "Implementation Fee (% of New
 *                 ARR)"). The take rate is NOT applied in this mode (Q-A): the percentage already is the expected fee.
 *
 * implementation_lines.impl_basis holds the answer in THREE states:
 *   NULL           never asked -- the legacy per-client method (every line stored before 2026-10-27)
 *   'per_client'   the per-client method, ANSWERED -- also how a line goes back from % mode: the PUT is COALESCE-on-omit and cannot
 *                  store NULL again (the 10-26 billing-terms lesson), so the off form is a stored value
 *   'pct_new_acv'  % of new ACV
 * implementation_lines.pct_of_new_acv holds the percentage as a FRACTION (0.2 = 20%), numeric(6,4): 4 decimals of the fraction =
 * 2 decimals of a percent (Q-D). NULL = unanswered; 0 is an ANSWER ("none"). It is required when the basis is 'pct_new_acv' -- judged
 * on the RESULTING row (a save changing only one of the two is judged against the stored other). A percentage stored on a per-client
 * line is accepted and inert (switching back and forth keeps the operator's number).
 *
 * A NEW line's basis starts BLANK and must be chosen before Save (Q-C) -- the page enforces it; the server does not require a basis
 * (a NULL basis is the legacy method, and the live screens send neither field).
 *
 * THE WALL: server/server.js mirrors IMPL_BASES and the three refusal functions below (implBasisRefusal / implPctRefusal /
 * implBasisResultingRefusal) -- verify-wall-mirrors pair M24 asserts they agree. The refusal messages are plain ASCII because the
 * server returns the same text. The typing helpers (parse / text) are the utils/implTakeRate.js shape: 0-100 typed, a fraction stored. */

import { parsePercentText, percentText } from './percentText';   // 2026-10-27: the typing rule's one owner

export const IMPL_BASIS_PER_CLIENT = 'per_client';
export const IMPL_BASIS_PCT_NEW_ACV = 'pct_new_acv';
export const IMPL_BASES = ['per_client', 'pct_new_acv'];

export const IMPL_BASIS_LABELS = { per_client: 'Per client (avg price x fee x take rate)', pct_new_acv: '% of new ACV' };
export const IMPL_BASIS_BLANK_LABEL = 'Choose a fee basis';
export const PCT_NEW_ACV_LABEL = '% of new ACV';
export const PCT_NEW_ACV_HELP = 'the new annual contract value booked that month';
export const IMPL_BASIS_REQUIRED_MESSAGE = 'Required \u2014 choose a fee basis';

/* the % mode's box, typed as a percent (the take-rate box's rules -- except a THIRD decimal is REFUSED rather than rounded: the column
   holds 2 decimals of a percent, and step 3 (Brian 10-27) refuses what it cannot store instead of changing the operator's number) */
export const PCT_NEW_ACV_RANGE_MESSAGE = '% of new ACV must be between 0% and 100%.';
export const PCT_NEW_ACV_PRECISION_MESSAGE = '% of new ACV takes at most two decimals (e.g. 17.25).';
export const PCT_NEW_ACV_REQUIRED_MESSAGE = 'Required \u2014 enter 0 for none';

/* the server's named refusals (step 1), in plain English for the line card -- shown beside Save, the draft kept */
const IMPL_BASIS_CODE_MESSAGES = {
  IMPL_BASIS_INVALID: 'Not saved: the fee basis was not recognised \u2014 choose Per client or % of new ACV.',
  IMPL_PCT_INVALID: 'Not saved: % of new ACV must be a number.',
  IMPL_PCT_OUT_OF_RANGE: 'Not saved: ' + PCT_NEW_ACV_RANGE_MESSAGE,
  IMPL_PCT_TOO_PRECISE: 'Not saved: ' + PCT_NEW_ACV_PRECISION_MESSAGE,
  IMPL_PCT_REQUIRED: 'Not saved: a line on % of new ACV needs its percentage \u2014 enter 0 for none.',
};
/** The plain-English message for one of the server's fee-basis codes, or null for any other code. */
export function implBasisErrorMessage(code) {
  return Object.prototype.hasOwnProperty.call(IMPL_BASIS_CODE_MESSAGES, code) ? IMPL_BASIS_CODE_MESSAGES[code] : null;
}

/** True when a line computes its fee as a percentage of new ACV. NULL / 'per_client' / anything else -> the per-client method. */
export function isPctNewAcvBasis(basis) {
  return basis === IMPL_BASIS_PCT_NEW_ACV;
}

// the typed text -> { rate: a fraction in [0, 1], error: null } | { rate: null, error: <message> }
// (2026-10-27: the rule itself is utils/percentText.js parsePercentText -- extracted from here unchanged so the Vendor Modal's
//  "% of Revenue" box reuses it; this box keeps its own three messages.)
export function parsePctNewAcvText(text) {
  return parsePercentText(text, { required: PCT_NEW_ACV_REQUIRED_MESSAGE, range: PCT_NEW_ACV_RANGE_MESSAGE, precision: PCT_NEW_ACV_PRECISION_MESSAGE });
}

// a stored fraction -> the box's text (0.2 -> "20", 0.1725 -> "17.25", 0 -> "0"); '' when there is no number
export function pctNewAcvText(rate) {
  return percentText(rate);
}

/* ---- THE RULES (mirrored on the server, pair M24). Each returns null (accepted) or { code, error }. ---- */

/** impl_basis as sent: omitted / null = keep (the PUT's COALESCE; the POST stores NULL); a present value must be in the set. */
export function implBasisRefusal(provided) {
  if (provided === undefined || provided === null) return null;
  if (typeof provided !== 'string' || !IMPL_BASES.includes(provided)) {
    return { code: 'IMPL_BASIS_INVALID', error: 'impl_basis must be one of ' + IMPL_BASES.join(', ') + ' (or omitted to keep the stored value)' };
  }
  return null;
}

/** pct_of_new_acv as sent: omitted / null = keep; a present value must be a finite NUMBER (a string is refused) in [0, 1] with at
 *  most 4 decimals (numeric(6,4) -- a 5th decimal would be rounded silently by the column, so it is refused instead). */
export function implPctRefusal(provided) {
  if (provided === undefined || provided === null) return null;
  if (typeof provided !== 'number' || !Number.isFinite(provided)) {
    return { code: 'IMPL_PCT_INVALID', error: 'pct_of_new_acv must be a number (a fraction: 0.2 = 20%)' };
  }
  if (provided < 0 || provided > 1) {
    return { code: 'IMPL_PCT_OUT_OF_RANGE', error: 'pct_of_new_acv must be between 0 and 1 (0% to 100%)' };
  }
  if (Math.abs(provided * 10000 - Math.round(provided * 10000)) > 1e-6) {
    return { code: 'IMPL_PCT_TOO_PRECISE', error: 'pct_of_new_acv has more than 4 decimals (2 decimals of a percent)' };
  }
  return null;
}

/** THE RESULTING ROW: the basis and the percentage the row will hold after the save (each: the value sent, else the stored one).
 *  '% of new ACV' needs a percentage; every other combination is accepted (a percentage on a per-client line is inert). */
export function implBasisResultingRefusal(basis, pct) {
  if (basis === IMPL_BASIS_PCT_NEW_ACV && (pct === null || pct === undefined)) {
    return { code: 'IMPL_PCT_REQUIRED', error: 'an implementation line on % of new ACV needs pct_of_new_acv (0 is an answer)' };
  }
  return null;
}
